import { useMemo, useCallback } from 'react';
import { Users } from 'lucide-react';
import { StepHeader } from '~/components/forms/StepHeader';
import { StudentInfoChunk } from '~/components/form-chunks/StudentInfoChunk';

interface AppStudentsStepProps {
  formData: any;
  gradeLevels: any[];
  updateFormData: (path: string, value: any) => void;
}

export function AppStudentsStep({ formData, gradeLevels, updateFormData }: AppStudentsStepProps) {
  // Merge persisted students and new students into a single array for the chunk
  const allStudents = useMemo(() => {
    const persisted = formData?.students || [];
    const newOnes = formData?.newStudents || [];
    return [...persisted, ...newOnes];
  }, [formData?.students, formData?.newStudents]);

  const handleUpdate = useCallback((updatedStudents: any[]) => {
    // Split back into persisted (have studentEnrollmentId) and new (tempId only)
    const persisted = updatedStudents.filter((s: any) => s.studentEnrollmentId);
    const newOnes = updatedStudents.filter((s: any) => !s.studentEnrollmentId);

    // Check if any persisted students were removed
    const currentPersisted = formData?.students || [];
    const removedIds = currentPersisted
      .filter((s: any) => s.studentEnrollmentId && !persisted.find((u: any) => u.studentEnrollmentId === s.studentEnrollmentId))
      .map((s: any) => s.studentEnrollmentId);

    if (removedIds.length > 0) {
      const currentRemovals = formData?.studentsToRemove || [];
      updateFormData('studentsToRemove', [...currentRemovals, ...removedIds]);
    }

    updateFormData('students', persisted);
    updateFormData('newStudents', newOnes);
  }, [formData?.students, formData?.studentsToRemove, updateFormData]);

  return (
    <div className="space-y-6">
      <StepHeader
        icon={Users}
        title="Students"
        description="Add the students you would like to enroll at our school."
      />

      <StudentInfoChunk
        students={allStudents}
        onUpdate={handleUpdate}
        gradeLevels={gradeLevels}
      />
    </div>
  );
}
